import { buildApiUrl } from '../../config/env';
import { storage } from '../../utils/storage';
import { appendFileToFormData } from '../../utils/uploadHelper';

export interface ReferralRequest {
  id: string;
  clubId: string;
  userId: string;
  businessName: string | null;
  businessDetails: string | null;
  lookingFor: string | null;
  videoUrl?: string | null;
  status: string;
  createdAt: string;
  updatedAt: string;
  user: {
    id: string;
    name: string | null;
    photo: string | null;
    businessName: string | null;
    profession: string | null;
  };
  responses?: ReferralResponse[];
}

export interface ReferralResponse {
  id: string;
  requestId: string;
  responderId: string;
  contactName: string;
  contactPhone: string;
  contactDetails: string;
  videoUrl?: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  responder: {
    id: string;
    name: string | null;
    photo: string | null;
    businessName: string | null;
  };
  request?: ReferralRequest;
}

export interface LeaderboardEntry {
  rank: number;
  userId: string;
  points: number;
  user: {
    id: string;
    name: string | null;
    photo: string | null;
    businessName: string | null;
    profession: string | null;
  };
}

// Helper function to make authenticated API calls
const apiCall = async (endpoint: string, options: RequestInit = {}) => {
  const token = await storage.getToken();
  const response = await fetch(buildApiUrl(endpoint), {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...options.headers,
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Request failed');
  }

  return response.json();
};

// Get all referral requests for a club
export const getReferralRequests = async (clubId: string): Promise<ReferralRequest[]> => {
  return apiCall(`/api/referrals/club/${clubId}`);
};

// Get my referral requests (ones I posted)
export const getMyReferralRequests = async (clubId: string): Promise<ReferralRequest[]> => {
  return apiCall(`/api/referrals/my-requests/${clubId}`);
};

// Get my intros (responses I submitted)
export const getMyIntros = async (clubId: string): Promise<ReferralResponse[]> => {
  return apiCall(`/api/referrals/my-intros/${clubId}`);
};

// Create a new referral request
export const createReferralRequest = async (data: {
  clubId: string;
  businessName?: string;
  businessDetails?: string;
  lookingFor?: string;
  videoUrl?: string;
}): Promise<ReferralRequest> => {
  return apiCall('/api/referrals', {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

const parseReferralError = async (response: Response, fallback: string): Promise<never> => {
  let message = fallback;
  try {
    const data = await response.json();
    message = data?.error || data?.message || fallback;
  } catch {
    // Keep the fallback if the body is not JSON.
  }
  throw new Error(message);
};

const appendReferralVideo = async (formData: FormData, videoUri: string) => {
  const filename = videoUri.split('/').pop() || 'referral-video.mp4';
  const match = /\.(\w+)$/.exec(filename);
  const ext = match ? match[1].toLowerCase() : 'mp4';
  const mimeType = ext === 'mov' ? 'video/quicktime' : `video/${ext}`;
  await appendFileToFormData(formData, 'video', videoUri, filename, mimeType);
};

export const createReferralVideoRequest = async (
  clubId: string,
  videoUri: string
): Promise<ReferralRequest> => {
  const token = await storage.getToken();
  if (!token) {
    throw new Error('Not authenticated');
  }

  const formData = new FormData();
  formData.append('clubId', clubId);
  await appendReferralVideo(formData, videoUri);

  const response = await fetch(buildApiUrl('/api/referrals/video'), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: formData,
  });

  if (!response.ok) {
    await parseReferralError(response, 'Failed to create video referral request');
  }

  return response.json();
};

export const updateReferralVideoRequest = async (
  requestId: string,
  videoUri: string
): Promise<ReferralRequest> => {
  const token = await storage.getToken();
  if (!token) {
    throw new Error('Not authenticated');
  }

  const formData = new FormData();
  await appendReferralVideo(formData, videoUri);

  const response = await fetch(buildApiUrl(`/api/referrals/${requestId}/video`), {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: formData,
  });

  if (!response.ok) {
    await parseReferralError(response, 'Failed to update video referral request');
  }

  return response.json();
};

// Update an existing referral request
export const updateReferralRequest = async (
  requestId: string,
  data: {
    businessName?: string;
    businessDetails?: string;
    lookingFor?: string;
    videoUrl?: string;
  }
): Promise<ReferralRequest> => {
  return apiCall(`/api/referrals/${requestId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
};

// Submit an intro (response to a referral request)
export const submitIntro = async (
  requestId: string,
  data: {
    contactName: string;
    contactPhone: string;
    contactDetails: string;
    familiarity?: string;
    videoUrl?: string;
  }
): Promise<ReferralResponse> => {
  return apiCall(`/api/referrals/respond/${requestId}`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

// Accept an intro
export const acceptIntro = async (responseId: string): Promise<ReferralResponse> => {
  return apiCall(`/api/referrals/accept/${responseId}`, { method: 'POST' });
};

// Reject an intro
export const rejectIntro = async (responseId: string): Promise<ReferralResponse> => {
  return apiCall(`/api/referrals/reject/${responseId}`, { method: 'POST' });
};

// Get leaderboard for a club (current month)
export const getLeaderboard = async (clubId: string): Promise<LeaderboardEntry[]> => {
  return apiCall(`/api/referrals/leaderboard/${clubId}`);
};

// Close a referral request
export const closeReferralRequest = async (requestId: string): Promise<ReferralRequest> => {
  return apiCall(`/api/referrals/close/${requestId}`, { method: 'POST' });
};
